import React, { useEffect, useRef, useState } from 'react';
import '../../styled/pages/dashboard.scss';
import { Header, Loader, Success, Failed } from '../../components';
import { customStyles } from '../../Utils/utils';

import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { updateReportTemplate } from '../../rtk-apis/reports/updateReportTemplate';
import { clearState as clearUpdateReportState } from '../../store/slices/reports/updateReportTemplate';
import { clearState as clearGetReportTemplateById } from '../../store/slices/reports/getReportTemplateById';

import { Node } from '@tiptap/core';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { EditorContent, useEditor } from '@tiptap/react';
import { extensions } from '../../Utils/reportExtension';
import { getReportTemplatesByIdApi } from '../../rtk-apis/reports/getReportTemplateById';
import BackButton from '../../components/BackButton';
import { useClinicDropdowns } from '../../Utils/useClinicDropdowns';
import {
  SUGGESTION_POPUP_HEIGHT,
  SUGGESTION_POPUP_WIDTH,
  getReportPopupPosition,
} from '../reports/components/reportPopupPosition';
import TemplateEditorToolbar from '../../components/TemplateEditorToolbar';
import ConfirmationModal from '../../components/ConfirmationModal';
import { getDefaultReportTemplateApi } from '../../rtk-apis/reports/getDefaultReportTemplate';
import { Box, Stack, Button } from '@mui/material';
import { cardiacTokens as T } from '../../theme/cardiacTheme';
import InsertDynamicFieldModal, {
  InsertDynamicFieldResult,
  DynamicFieldAutoFillSource,
  DynamicFieldEditValues,
} from '../../components/InsertDynamicFieldModal';
import { generateUniqueDynamicFieldKey } from '../../Utils/dynamicFieldKeys';
import { wrapDynamicField, DynamicFieldType } from '../../Utils/dynamicFieldMarkup';
import { findChainOccurrences } from '../../Utils/chainFieldSync';
import { applyDynamicFieldMetaUpdate } from '../../Utils/dynamicFieldSync';
import { CHAIN_FIELDS } from '../../Utils/measurementChain';

const TemplateModal = () => {
  const breadcrumbs = [{ label: 'Templates' }];
  const editorRef = useRef(null);

  ///////////////// States ///////////////////////////////
  const [template, setTemplate] = useState('');
  const [updatedTemplate, setUpdatedTemplate] = useState('');
  const [displayMessage, setDisplayMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [disableUpdateButton, setDisableUpdateButton] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);
  const [isInsertFieldModalOpen, setIsInsertFieldModalOpen] = useState(false);
  /**
   * Set when Insert Field is opened by clicking an EXISTING dynamic field span
   * (rather than the "Insert Field" button) — the dialog then edits this field's
   * definition in place instead of creating a new one. Cleared on cancel/save.
   */
  const [editingDynamicField, setEditingDynamicField] = useState<{
    fieldKey: string;
    values: DynamicFieldEditValues;
  } | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [currentTemplateName, setCurrentTemplateName] = useState('');

  // States for suggestion functionality
  const [suggestions, setSuggestions] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [selectedSpan, setSelectedSpan] = useState(null);

  ///////////////// Redux /////////////////////////////////
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { dropdownMap } = useClinicDropdowns();
  const dropdownMapRef = useRef(dropdownMap);
  dropdownMapRef.current = dropdownMap;

  const { status, result, msg, loading } = useSelector(
    (state: RootState) => state.updateReportTemplateResponse
  );
  const {
    status: getTemplateStatus,
    result: getTemplateResult,
    msg: getTemplateMessage,
    loading: getTemplateLoading,
  } = useSelector((state: RootState) => state.getReportTemplatesByIdResponse);

  ///////////////// EDITOR /////////////////////////////
  const editor = useEditor({
    extensions,
    content: '',
    editorProps: {
      attributes: {
        spellcheck: 'false',
      },
      handleClick: (view, pos, event) => {
        const dynamicFieldTarget = event.target.closest('span[data-dynamic-field]');
        if (dynamicFieldTarget) {
          event.stopPropagation();
          const key = dynamicFieldTarget.classList[0];
          if (!key) return;
          const label = dynamicFieldTarget.getAttribute('title') || key;
          const dynFieldType =
            (dynamicFieldTarget.getAttribute('data-field-type') as DynamicFieldType | null) || 'text';
          let dynOptions: string[] = [];
          const rawOptions = dynamicFieldTarget.getAttribute('data-options');
          if (rawOptions) {
            try {
              dynOptions = JSON.parse(rawOptions);
            } catch {
              dynOptions = [];
            }
          }
          setEditingDynamicField({
            fieldKey: key,
            values: {
              label,
              fieldType: dynFieldType,
              options: dynOptions,
              unit: dynamicFieldTarget.getAttribute('data-unit') || undefined,
              srConcept: dynamicFieldTarget.getAttribute('data-sr-concept') || undefined,
            },
          });
          setIsInsertFieldModalOpen(true);
          return;
        }
        const target = event.target.closest('span[data-editable]');
        if (!target) return;
        event.stopPropagation();
        const spanClass = target.classList[0];
        if (!spanClass) return;
        const editorPos = view.posAtDOM(target, 0);
        setSuggestions(dropdownMapRef.current[spanClass] || []);
        setSelectedSpan({
          from: editorPos,
          to: editorPos + target.textContent.length,
          className: spanClass,
          element: target,
        });
        setDropdownPosition(
          getReportPopupPosition(
            target as HTMLElement,
            null,
            SUGGESTION_POPUP_WIDTH,
            SUGGESTION_POPUP_HEIGHT
          )
        );
      },
    },
  });

  // Function to handle word replacement
  const handleReplaceWord = suggestion => {
    if (editor && selectedSpan) {
      const scrollPosition = editor.view.dom.scrollTop;
      editor
        .chain()
        .deleteRange({ from: selectedSpan.from, to: selectedSpan.to })
        .insertContentAt(
          selectedSpan.from,
          `<span data-editable class='${selectedSpan.className}'>${suggestion}</span>`
        )
        .setTextSelection(selectedSpan.from + suggestion.length)
        .run();
      setTimeout(() => {
        editor.view.dom.scrollTop = scrollPosition;
      }, 0);
      setDropdownPosition(null);
    }
  };

  // Effect for handling clicks outside the suggestion dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        !event.target.closest('.suggestion-dropdown') &&
        !event.target.closest('span[data-editable]')
      ) {
        setDropdownPosition(null);
        setSelectedSpan(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  ///////////////// side effects functions /////////////////////////////
  useEffect(() => {
    getUpdatedTempalte();
  }, []);

  useEffect(() => {
    if (editor && template) {
      // `addToHistory: false` keeps the load itself out of the undo stack.
      //
      // The editor mounts empty and the template arrives from the fetch afterwards, so
      // loading it was an ordinary undoable step: Ctrl+Z walked back past it and left the
      // clinic staring at a blank template. The loaded template is the baseline now, and
      // undo can only reach edits made on top of it.
      editor.chain().setMeta('addToHistory', false).setContent(template).run();
    }
  }, [editor, template]);

  useEffect(() => {
    const handleSuccess = message => {
      setTemplate(getTemplateResult?.data?.templateHTML);
      // Captured here, not read from getTemplateResult later: this effect's
      // own cleanup unconditionally dispatches clearGetReportTemplateById(),
      // which wipes the slice back to '' shortly after this fires — nothing
      // downstream ever needed the fetched data again until Restore Default,
      // which found `getTemplateResult` already empty by the time it read it.
      setCurrentTemplateName(getTemplateResult?.data?.templateName || '');
      setDisableUpdateButton(false);
    };

    const handleFailure = message => {
      setDisplayMessage(message);
      setIsFailedModalOpen(true);
      setDisableUpdateButton(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    };
    if (getTemplateStatus === 'SUCCESS') {
      handleSuccess(getTemplateMessage);
    }

    if (getTemplateStatus === 'FAILED') {
      handleFailure(getTemplateMessage);
    }

    return () => {
      dispatch(clearGetReportTemplateById());
    };
  }, [getTemplateStatus]);

  const getCustomHTML = () => {
    const editorElement = editor.view.dom; // Get the root DOM node of the editor
    const clonedElement = editorElement.cloneNode(true); // Clone to prevent modifying the original

    // Remove unwanted attributes from <span data-editable> elements
    clonedElement.querySelectorAll('span[data-editable]').forEach(el => {
      el.removeAttribute('style'); // Remove inline styles
      el.removeAttribute('onmouseenter'); // Remove event listeners
      el.removeAttribute('onmouseleave');

      // Ensure data-editable does not have an empty value
      if (el.getAttribute('data-editable') === '') {
        el.removeAttribute('data-editable'); // Remove existing
        el.setAttribute('data-editable', ''); // Add back without a value
      }
    });
    // Remove <br class="ProseMirror-trailingBreak"> tags
    clonedElement.querySelectorAll('br.ProseMirror-trailingBreak').forEach(br => {
      br.remove();
    });

    return clonedElement.innerHTML; // Convert to string
  };

  useEffect(() => {
    if (editor) {
      editor.on('update', () => {
        const updatedContent = getCustomHTML(); // Get content as HTML
        // const updatedContent = editor.getHTML();
        console.log('🚀 ~ editor.on ~ updatedContent:', updatedContent);
        setUpdatedTemplate(updatedContent);
      });
    }
  }, [editor]);

  useEffect(() => {
    const handleSuccess = message => {
      setDisplayMessage(message);
      setIsSuccessModalOpen(true);
      setTimeout(() => {
        setIsSuccessModalOpen(false);
        setDisplayMessage('');
        getUpdatedTempalte(); // hit the ID API here
      }, 1500);
    };

    const handleFailure = message => {
      setDisplayMessage(message);
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    };
    if (status === 'SUCCESS') {
      handleSuccess(msg);
    }

    if (status === 'FAILED') {
      handleFailure(msg);
    }

    return () => {
      dispatch(clearUpdateReportState());
    };
  }, [status]);

  /**
   * Track A, Phase 6 — "Restore Default" (12 Aug call, 35:54–36:18): loads
   * the seed markup for this template's name into the editor. Nothing is
   * persisted here — the clinic user still has to click Update to save it,
   * same as any other edit, so a restore can itself be undone by navigating
   * away without saving.
   */
  const handleInsertDynamicField = ({
    fieldKey,
    label,
    fieldType,
    options,
    unit,
    initialValue,
    srConcept,
  }: InsertDynamicFieldResult) => {
    if (!editor) {
      return;
    }
    if (editingDynamicField) {
      applyDynamicFieldMetaUpdate(editor, fieldKey, { label, fieldType, options, unit, srConcept });
      setEditingDynamicField(null);
      setIsInsertFieldModalOpen(false);
      return;
    }
    editor
      .chain()
      .focus()
      .insertContent(
        wrapDynamicField(fieldKey, label, initialValue || '', { fieldType, options, unit, srConcept })
      )
      .run();
    setIsInsertFieldModalOpen(false);
  };

  /**
   * Measurements already placed in this template, read-only, offered as
   * auto-fill sources for a new number dynamic field. Deduped by key — a
   * value can be dropped into a template more than once.
   */
  const getAutoFillSources = (): DynamicFieldAutoFillSource[] => {
    if (!editor) {
      return [];
    }
    const seen = new Map<string, DynamicFieldAutoFillSource>();
    findChainOccurrences(editor).forEach(({ key, text }) => {
      if (seen.has(key) || !text.trim()) {
        return;
      }
      const spec = CHAIN_FIELDS[key];
      if (!spec) {
        return;
      }
      seen.set(key, { key, label: spec.label, value: text });
    });
    return Array.from(seen.values());
  };

  const handleRestoreDefault = async () => {
    // ConfirmationModal doesn't disable its own button while the async work
    // below is in flight (only the label text changes to "Restoring…") —
    // guard against a rapid double-click firing this twice concurrently.
    if (isRestoring) {
      return;
    }
    if (!editor || !currentTemplateName) {
      setDisplayMessage("Could not determine this template's name — cannot restore.");
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
      setIsRestoreConfirmOpen(false);
      return;
    }
    setIsRestoring(true);
    try {
      const result = await dispatch(
        getDefaultReportTemplateApi({ templateName: currentTemplateName })
      ).unwrap();
      const defaultHTML = result?.data?.templateHTML;
      if (defaultHTML) {
        editor.commands.setContent(defaultHTML);
      } else {
        setDisplayMessage('No default template found for this template name.');
        setIsFailedModalOpen(true);
        setTimeout(() => {
          setIsFailedModalOpen(false);
          setDisplayMessage('');
        }, 1500);
      }
    } catch (err) {
      setDisplayMessage(typeof err === 'string' ? err : 'Failed to fetch default template.');
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    } finally {
      setIsRestoring(false);
      setIsRestoreConfirmOpen(false);
    }
  };

  //////////////////// handler Functions //////////////////
  const updateTemplate = () => {
    console.log('updatetemplate', updatedTemplate);
    dispatch(
      updateReportTemplate({
        templateId: location?.state?._id,
        templateHTML: updatedTemplate,
      })
    );
  };

  const getUpdatedTempalte = () => {
    dispatch(
      getReportTemplatesByIdApi({
        templateId: id,
      })
    );
  };

  return (
    <section
      className="layoutContainer"
      style={customStyles.layoutBackground}
    >
      <div className="layoutGrid">
        <div className="page">
          <Header />
          <div className="page__body">
            <Box sx={{ mb: 2 }}>
              <BackButton />
            </Box>
            {/*
              Sticky positioning lives on this plain outer box; the rounded corners
              and overflow:hidden live on the inner one. Putting both on the same
              element lets the scrolled template content bleed through at the top
              edge while scrolling — a known Chromium quirk with
              position:sticky + overflow:hidden + border-radius together.
            */}
            <Box sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <Box
              sx={{
                backgroundColor: T.surface,
                borderRadius: '10px',
                border: `1px solid ${T.line}`,
                mb: 2,
                overflow: 'hidden',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${T.line}` }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Button
                    variant="text"
                    onClick={() => {
                      setEditingDynamicField(null);
                      setIsInsertFieldModalOpen(true);
                    }}
                    sx={{
                      color: T.ink2,
                      textTransform: 'none',
                      fontWeight: 400,
                      px: 1,
                      '&:hover': { color: T.ink, backgroundColor: 'rgba(255, 255, 255, 0.04)' },
                    }}
                  >
                    Insert Field
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setIsRestoreConfirmOpen(true)}
                    sx={{
                      color: T.ink2,
                      textTransform: 'none',
                      fontWeight: 400,
                      px: 1,
                      '&:hover': { color: T.ink, backgroundColor: 'rgba(255, 255, 255, 0.04)' },
                    }}
                  >
                    Restore Default
                  </Button>
                </Stack>
                <Button
                  variant="contained"
                  onClick={updateTemplate}
                  disabled={disableUpdateButton}
                  sx={{
                    backgroundColor: T.teal,
                    color: T.bg,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: T.teal2, boxShadow: 'none' },
                  }}
                >
                  Update
                </Button>
              </Stack>
              <Box sx={{ overflowX: 'auto' }}>
                <TemplateEditorToolbar editor={editor} />
              </Box>
            </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <div
                className="whiteTemplateBox"
                style={{ position: 'relative', maxWidth: '900px', width: '100%' }}
                ref={editorRef}
              >
                <EditorContent editor={editor} />
              {dropdownPosition && suggestions.length > 0 && (
                <div
                  className="suggestion-dropdown"
                  style={{
                    // Viewport coordinates, so a scrolled preview cannot move the
                    // menu away from the word that opened it.
                    position: 'fixed',
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    borderRadius: '4px',
                    color: 'black',
                    zIndex: 1000,
                    padding: '3px',
                    minWidth: '50px',
                    maxWidth: '350px', // Max width
                    maxHeight: '200px', // Max height before scrolling
                    overflowY: 'auto', // Enables scrolling if content overflows
                  }}
                >
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleReplaceWord(suggestion)}
                      style={{
                        padding: '4px',
                        cursor: 'pointer',
                        borderBottom: index !== suggestions.length - 1 ? '1px solid #ddd' : 'none',
                        fontSize: '12px',
                      }}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
              </div>
            </Box>
          </div>
        </div>
      </div>
      <Loader isLoading={loading || getTemplateLoading} />
      <Success
        handleCloseModal={() => setIsSuccessModalOpen(false)}
        title={displayMessage}
        isSuccessModalOpen={isSuccessModalOpen}
      />
      <Failed
        handleCloseModal={() => setIsFailedModalOpen(false)}
        title={displayMessage}
        isFailedModalOpen={isFailedModalOpen}
      />
      <ConfirmationModal
        isConfirmationModalOpen={isRestoreConfirmOpen}
        title="Restore default template?"
        description="This replaces everything currently in the editor with this clinic's original template. Nothing is saved until you click Update — you can still navigate away without saving to undo this."
        confirmActionText={isRestoring ? 'Restoring…' : 'Restore'}
        cancelActionText="Cancel"
        handleCloseModal={() => setIsRestoreConfirmOpen(false)}
        handleConfirm={handleRestoreDefault}
        destructive
      />
      <InsertDynamicFieldModal
        isOpen={isInsertFieldModalOpen}
        onCancel={() => {
          setIsInsertFieldModalOpen(false);
          setEditingDynamicField(null);
        }}
        onConfirm={handleInsertDynamicField}
        previewFieldKey={label => (editor ? generateUniqueDynamicFieldKey(editor, label) : '')}
        autoFillSources={getAutoFillSources()}
        editingFieldKey={editingDynamicField?.fieldKey}
        editingValues={editingDynamicField?.values}
      />
    </section>
  );
};

export default TemplateModal;
